/**
 * Turn alerts on this phone (D-084): a push subscription the server uses for "your turn" while you're away and for any
 * notification that finds no open app. The service worker (`/sw.js`) only shows notifications; it caches nothing.
 * iPhone allows it only when DIGU is added to the Home Screen; elsewhere where it isn't supported this does nothing.
 */
import { api } from "./api";

export const pushSupported = (): boolean =>
  typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && typeof Notification !== "undefined";

const urlKey = (base64: string): Uint8Array => {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

/** Registers the worker; when notifications are already allowed, makes sure the server has this phone's subscription. */
export async function startPush(): Promise<void> {
  if (!pushSupported()) return;
  try {
    const reg = await navigator.serviceWorker.register("/sw.js");
    if (Notification.permission === "granted") await subscribe(reg);
  } catch { /* push is a bonus; the app works without it */ }
}

/**
 * Asks once, from a tap (iPhone requires a gesture): call it first thing in the click handler, before any await.
 * Resolves true when this phone will get turn alerts.
 */
export function enableTurnAlerts(): Promise<boolean> {
  if (!pushSupported() || Notification.permission === "denied") return Promise.resolve(false);
  const asked = Notification.permission === "granted" ? Promise.resolve("granted" as NotificationPermission) : Notification.requestPermission();
  return asked.then(async (p) => {
    if (p !== "granted") return false;
    try { await subscribe(await navigator.serviceWorker.register("/sw.js")); return true; } catch { return false; }
  });
}

async function subscribe(reg: ServiceWorkerRegistration): Promise<void> {
  const { publicKey } = await api.get<{ publicKey: string }>("/push/key");
  const existing = await reg.pushManager.getSubscription();
  const sub = existing ?? await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlKey(publicKey).buffer as ArrayBuffer });
  const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
}
